import React from 'react';
import { FormState } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CreateJobDto } from '../../../../../../services/applicant_tracking/JobService/CreateJobDto';
import {
  SetupHeaderButtonType,
  SetupPageHeader,
} from '../../../../../../components/SetupPageHeader';
import { replaceFinalSegment } from '../../../../../../utils/url';
import { useStateMachine } from 'little-state-machine';
import { JobDetails } from '../../../../../../services/applicant_tracking/JobService/EditJobDto';
import { SetupPageFooter } from '../../../../../../components/SetupPageFooter';
import { DropdownActionButton } from '../../../../../../components/DropdownActionButton';

const PUBLISH_DROPDOWN_OPTIONS = [
  {
    title: 'Publish Job',
    description:
      'Publish the job on your Careers Page and, if chosen, on free job boards, allowing candidates to apply.',
  },
  {
    title: 'Save as Open',
    description:
      'The job will be active but not visible on your careers page so candidates cannot apply.',
  },
  {
    title: 'Save as Draft',
    description:
      "The job setup isn't complete yet and won't be active until you finalise it.",
  },
];

interface PropTypes {
  setupStep: number;
  formState: FormState<CreateJobDto>;
  isEditFlow: boolean;
  jobDetails: JobDetails;
  indeedFormValid: boolean;
  organicPostValid: boolean;
  organicEnabled: boolean;
  indeedEnabled: boolean;
  isLoading: boolean;
  isSaving: boolean;
  setSetupStep: (newStep: number, jobDTO: CreateJobDto) => void;
  onCancel: () => void;
  onSave: () => void;
  onSaveAsDraft: () => void;
  onSubmit: () => void;
  onNavigate: () => void;
  children: React.ReactNode;
}

export function JobOrganicPostsLayout(props: PropTypes) {
  const navigate = useNavigate();
  const { state } = useStateMachine();
  const jobPublished = props.jobDetails?.status === 'published';
  // Show dropdown for new or unpublished jobs
  const showDropdown = !jobPublished || !props.isEditFlow;

  const dropdownDisabled =
    props.isSaving ||
    Object.keys(props.formState.errors).length > 0 ||
    !props.organicPostValid ||
    !props.indeedFormValid;

  const jobOpen = props.jobDetails?.status === 'open';

  const dropdownOptions = [
    { ...PUBLISH_DROPDOWN_OPTIONS[0], action: props.onSubmit },
    { ...PUBLISH_DROPDOWN_OPTIONS[1], action: props.onSave },
    // "Save as Draft" is only available for new jobs or draft jobs
    ...(!jobOpen
      ? [{ ...PUBLISH_DROPDOWN_OPTIONS[2], action: props.onSaveAsDraft }]
      : []),
  ];

  const renderPublishDropdown = (direction?: 'up' | 'down') => (
    <DropdownActionButton
      label='Publish Job'
      disabled={dropdownDisabled}
      isLoading={props.isSaving}
      direction={direction}
      options={dropdownOptions}
    />
  );

  const saveAndPublishButton: SetupHeaderButtonType = {
    title: 'Save and Publish',
    type: 'Save',
    disabled: dropdownDisabled,
    isLoading: props.isSaving,
    action: props.onSubmit,
  };

  const cancelButton: SetupHeaderButtonType = {
    title: 'Cancel',
    type: 'Cancel',
    action: props.onCancel,
  };

  return (
    <>
      <SetupPageHeader
        title='Free Job Boards'
        subTitle={
          <span className='fs-5 text-dark-200'>
            Expand your reach at no cost. Fill all job details to maximize
            visibility.
          </span>
        }
        titleClassNames='mb-3'
        firstItems={[
          {
            title: 'Previous',
            type: 'Navigate',
            action: () => {
              props.onNavigate();
              props.setSetupStep(props.setupStep - 1, state.setupJob);
              navigate(replaceFinalSegment('job_workflow'));
            },
            disabled: Object.keys(props.formState.errors).length > 0,
          },
        ]}
        intermediateItems={[cancelButton]}
        customLastItem={showDropdown ? renderPublishDropdown() : null}
        lastItem={!showDropdown ? saveAndPublishButton : undefined}
      />
      {props.children}
      {!props.isLoading && (
        <SetupPageFooter
          classNames='mt-4'
          customFirstItem={
            showDropdown ? renderPublishDropdown('up') : undefined
          }
          firstItem={!showDropdown ? saveAndPublishButton : undefined}
          lastItem={cancelButton}
        />
      )}
    </>
  );
}
